import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Bot, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { PROVIDERS, type ProviderId } from "~/lib/onboarding/constants";
import { $saveProviderConnection } from "~/lib/onboarding/mutations";
import { getBoolean, getNumber, getString, isRecord } from "~/lib/type-guards";

type DeviceCodeData = {
  device_code: string;
  user_code: string;
  verification_uri: string;
  expires_in: number;
  interval: number;
};

type PollTokenData = {
  access_token: string;
};

type SaveConnectionResult = {
  connectionId: string;
  isNew: boolean;
};

export const Route = createFileRoute("/(authenticated)/dashboard/settings/providers/add")(
  {
    component: AddProviderPage,
  },
);

function AddProviderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState<ProviderId | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [githubOAuthStep, setGithubOAuthStep] = useState<
    "idle" | "waiting-for-auth" | "polling" | "success"
  >("idle");
  const [githubUserCode, setGithubUserCode] = useState<string | null>(null);
  const [githubVerificationUri, setGithubVerificationUri] = useState<string | null>(null);

  const availableProviders = PROVIDERS.filter((p) => !p.isSystemManaged);
  const provider = availableProviders.find((p) => p.id === selectedProvider);

  const handleGitHubAuth = async () => {
    if (selectedProvider !== "github-copilot") return;

    setIsLoading(true);
    setError(null);
    setGithubOAuthStep("waiting-for-auth");

    try {
      const deviceCodeResponse = await fetch("/api/providers?action=request-code");
      if (!deviceCodeResponse.ok) {
        throw new Error("Failed to request device code");
      }
      const deviceCodeUnknown: unknown = await deviceCodeResponse.json();
      if (!isRecord(deviceCodeUnknown)) {
        throw new Error("Invalid device code response");
      }

      const deviceCodeData: DeviceCodeData = {
        device_code: getString(deviceCodeUnknown, "device_code") ?? "",
        user_code: getString(deviceCodeUnknown, "user_code") ?? "",
        verification_uri: getString(deviceCodeUnknown, "verification_uri") ?? "",
        expires_in: getNumber(deviceCodeUnknown, "expires_in") ?? 0,
        interval: getNumber(deviceCodeUnknown, "interval") ?? 0,
      };

      if (
        !deviceCodeData.device_code ||
        !deviceCodeData.user_code ||
        !deviceCodeData.verification_uri ||
        !deviceCodeData.interval
      ) {
        throw new Error("Invalid device code response");
      }

      setGithubUserCode(deviceCodeData.user_code);
      setGithubVerificationUri(deviceCodeData.verification_uri);

      window.open(deviceCodeData.verification_uri, "_blank");

      setGithubOAuthStep("polling");

      const tokenResponse = await fetch(
        `/api/providers?action=poll-token&device_code=${encodeURIComponent(
          deviceCodeData.device_code,
        )}&interval=${deviceCodeData.interval}`,
      );
      if (!tokenResponse.ok) {
        throw new Error("Failed to get access token");
      }
      const tokenData = await tokenResponse.json();

      const tokenDataUnknown: unknown = tokenData;
      if (!isRecord(tokenDataUnknown)) {
        throw new Error("Invalid token response");
      }
      const pollTokenData: PollTokenData = {
        access_token: getString(tokenDataUnknown, "access_token") ?? "",
      };
      if (!pollTokenData.access_token) {
        throw new Error("Invalid token response");
      }

      setGithubOAuthStep("success");

      const saveResponse = await fetch("/api/providers?action=save-connection", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          githubAccessToken: pollTokenData.access_token,
        }),
      });

      if (!saveResponse.ok) {
        throw new Error("Failed to save connection");
      }

      const resultUnknown: unknown = await saveResponse.json();
      if (!isRecord(resultUnknown)) {
        throw new Error("Invalid save response");
      }
      const result: SaveConnectionResult = {
        connectionId: getString(resultUnknown, "connectionId") ?? "",
        isNew: getBoolean(resultUnknown, "isNew") ?? false,
      };
      if (!result.connectionId) {
        throw new Error("Invalid save response");
      }

      queryClient.invalidateQueries({ queryKey: ["ai-provider-connections"] });
      toast.success(
        result.isNew
          ? "GitHub Copilot connected successfully"
          : "GitHub Copilot connection updated",
      );
      navigate({ to: "/dashboard/settings/providers" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to authenticate with GitHub");
      setGithubOAuthStep("idle");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderClick = (providerId: ProviderId) => {
    setSelectedProvider(providerId);
    const p = availableProviders.find((p) => p.id === providerId);
    setDisplayName(p?.name || "");
    setApiKey("");
    setError(null);
    setGithubOAuthStep("idle");
    setGithubUserCode(null);
    setGithubVerificationUri(null);
  };

  const saveMutation = useMutation({
    mutationFn: async (data: {
      provider: ProviderId;
      displayName: string;
      apiKey?: string;
      isDefault: boolean;
    }) => {
      await $saveProviderConnection({ data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-provider-connections"] });
      toast.success("Provider added successfully");
      navigate({ to: "/dashboard/settings/providers" });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save provider");
    },
  });

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!selectedProvider || !displayName) return;

    if (provider?.requiresApiKey && !apiKey) {
      setError("API key is required");
      return;
    }

    saveMutation.mutate({
      provider: selectedProvider,
      displayName,
      apiKey: apiKey || undefined,
      isDefault,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate({ to: "/dashboard/settings/providers" })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Add AI Provider</h2>
          <p className="text-muted-foreground">
            Select a provider and enter your API key
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {availableProviders.map((p) => (
          <Card
            key={p.id}
            className={`hover:border-primary/50 cursor-pointer transition-all ${
              selectedProvider === p.id ? "border-primary ring-primary/20 ring-2" : ""
            }`}
            onClick={() => handleProviderClick(p.id)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="bg-muted mb-3 flex size-10 items-center justify-center rounded-lg">
                  <Bot className="text-foreground size-5" />
                </div>
                {selectedProvider === p.id && (
                  <div className="bg-primary flex size-5 items-center justify-center rounded-full">
                    <Check className="text-primary-foreground size-3" />
                  </div>
                )}
              </div>
              <CardTitle className="text-lg">{p.name}</CardTitle>
              <CardDescription>{p.description}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>

      {selectedProvider && (
        <Card>
          <CardHeader>
            <CardTitle>Provider Configuration</CardTitle>
            <CardDescription>Configure your {provider?.name} connection</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit} className="space-y-4 p-6 pt-0">
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={`My ${provider?.name} Account`}
              />
            </div>

            {provider?.requiresApiKey && (
              <div className="space-y-2">
                <Label htmlFor="apiKey">API Key</Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={
                    selectedProvider === "openai"
                      ? "sk-..."
                      : selectedProvider === "anthropic"
                        ? "sk-ant-..."
                        : "sk-or-..."
                  }
                />
              </div>
            )}

            {selectedProvider === "github-copilot" && (
              <div className="bg-muted/50 space-y-3 rounded-lg border p-4">
                {githubOAuthStep === "idle" && (
                  <>
                    <p className="text-muted-foreground text-sm">
                      Click the button below to authenticate with GitHub. A new window
                      will open where you can authorize access.
                    </p>
                    <Button
                      type="button"
                      onClick={handleGitHubAuth}
                      disabled={isLoading}
                      className="w-full"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        "Connect with GitHub"
                      )}
                    </Button>
                  </>
                )}
                {githubOAuthStep === "waiting-for-auth" && (
                  <div className="text-center">
                    <Loader2 className="mx-auto mb-2 size-6 animate-spin" />
                    <p className="text-sm font-medium">Requesting authorization...</p>
                  </div>
                )}
                {githubOAuthStep === "polling" &&
                  githubUserCode &&
                  githubVerificationUri && (
                    <div className="space-y-3 text-center">
                      <Loader2 className="mx-auto mb-2 size-6 animate-spin" />
                      <div>
                        <p className="text-sm font-medium">
                          Waiting for authorization...
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          If the window didn't open, visit:
                        </p>
                        <a
                          href={githubVerificationUri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary mt-1 block text-sm font-medium hover:underline"
                        >
                          {githubVerificationUri}
                        </a>
                      </div>
                      <div className="bg-muted rounded p-2">
                        <p className="text-muted-foreground text-xs">Enter this code:</p>
                        <p className="font-mono text-lg font-bold">{githubUserCode}</p>
                      </div>
                    </div>
                  )}
                {githubOAuthStep === "success" && (
                  <div className="text-center text-green-600">
                    <Check className="mx-auto mb-2 size-6" />
                    <p className="text-sm font-medium">Authorization successful!</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="isDefault"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked === true)}
              />
              <Label htmlFor="isDefault" className="cursor-pointer text-sm">
                Set as default provider
              </Label>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/dashboard/settings/providers" })}
              >
                Cancel
              </Button>
              {selectedProvider !== "github-copilot" && (
                <Button type="submit" disabled={saveMutation.isPending || !displayName}>
                  {saveMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Provider"
                  )}
                </Button>
              )}
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
